import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Plus, FolderKanban, MoreHorizontal, Trash2, Settings } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/config/api';

export const ProjectsPage = () => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, [token]);

  const fetchProjects = async () => {
    if (!token) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setProjects(data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateProject = async () => {
    if (!newProjectName.trim()) return;
    
    setIsCreating(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newProjectName,
          description: newProjectDesc
        })
      });
      
      if (response.ok) {
        const project = await response.json();
        setProjects(prev => [project, ...prev]);
        setIsCreateOpen(false);
        setNewProjectName('');
        setNewProjectDesc('');
        toast.success('Project created!');
        navigate(`/projects/${project.id}`);
      } else {
        toast.error('Failed to create project');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to create project');
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteProject = async (projectId, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this project? This action cannot be undone.')) {
      return;
    }
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/projects/${projectId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        setProjects(prev => prev.filter(p => p.id !== projectId));
        toast.success('Project deleted');
      } else {
        toast.error('Failed to delete project');
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      toast.error('Failed to delete project');
    }
  };

  const getCardCount = (project) => {
    return project.columns?.reduce((acc, col) => acc + (col.cards?.length || 0), 0) || 0;
  };

  if (isLoading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-[#1a1d21]">
        <div className="text-gray-400">Loading projects...</div>
      </div>
    );
  }

  return (
    <div className="flex-1 bg-[#1a1d21] min-h-screen p-4 sm:p-6 lg:p-8 min-w-0" data-testid="projects-page">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1 sm:mb-2">Projects</h1>
        <p className="text-sm sm:text-base text-gray-400">Manage your Kanban boards and tasks</p>
      </div>

      {/* Projects Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {/* Create New Project Card */}
        <button
          onClick={() => setIsCreateOpen(true)}
          className="group h-48 bg-[#0F2942]/50 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center gap-3 hover:border-blue-500/50 hover:bg-[#0F2942] transition-all"
          data-testid="create-project-btn"
        >
          <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center group-hover:bg-blue-500/30 transition-colors">
            <Plus size={24} className="text-blue-400" />
          </div>
          <span className="text-gray-400 group-hover:text-white transition-colors">Create New Project</span>
        </button>

        {/* Project Cards */}
        {projects.map(project => (
          <div
            key={project.id}
            onClick={() => navigate(`/projects/${project.id}`)}
            className="group h-48 bg-[#0F2942] rounded-xl p-5 cursor-pointer hover:bg-[#0F2942]/80 transition-all border border-transparent hover:border-blue-500/30"
            data-testid={`project-card-${project.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                  <FolderKanban size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-semibold truncate max-w-[150px]">{project.name}</h3>
                  <p className="text-xs text-gray-400">{getCardCount(project)} cards</p>
                </div>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity touch-manipulation">
                    <MoreHorizontal size={16} className="text-gray-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-[#1a1d21] border-white/20">
                  <DropdownMenuItem className="text-gray-300 hover:text-white focus:bg-white/10">
                    <Settings size={14} className="mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="text-red-400 hover:text-red-300 focus:bg-red-500/10"
                    onClick={(e) => handleDeleteProject(project.id, e)}
                  >
                    <Trash2 size={14} className="mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            {project.description && (
              <p className="text-sm text-gray-400 line-clamp-2 mb-3">{project.description}</p>
            )}
            
            {/* Column Preview */}
            <div className="flex gap-2 mt-auto">
              {project.columns?.slice(0, 3).map(col => (
                <div key={col.id} className="flex-1">
                  <div 
                    className="h-1 rounded-full mb-1" 
                    style={{ backgroundColor: col.color }}
                  />
                  <p className="text-[10px] text-gray-500 truncate">{col.title}</p>
                </div>
              ))}
            </div>
            
            {/* Members */}
            {project.members?.length > 0 && (
              <div className="flex -space-x-2 mt-4">
                {project.members.slice(0, 4).map((member, i) => (
                  <div
                    key={member.user_id}
                    className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-medium border-2 border-[#0F2942]"
                    style={{ backgroundColor: member.avatar_color }}
                    title={member.name}
                  >
                    {member.name?.charAt(0).toUpperCase()}
                  </div>
                ))}
                {project.members.length > 4 && (
                  <div className="w-7 h-7 rounded-full bg-gray-600 flex items-center justify-center text-white text-xs border-2 border-[#0F2942]">
                    +{project.members.length - 4}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Empty State */}
      {projects.length === 0 && (
        <div className="text-center py-16">
          <FolderKanban size={64} className="mx-auto text-gray-600 mb-4" />
          <h3 className="text-xl text-gray-400 mb-2">No projects yet</h3>
          <p className="text-gray-500 mb-6">Create your first project to get started with Kanban boards</p>
          <Button onClick={() => setIsCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus size={18} className="mr-2" />
            Create Project
          </Button>
        </div>
      )}

      {/* Create Project Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-[#0F2942] border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Create a new project</DialogTitle>
            <DialogDescription className="text-gray-400">
              Projects help you organize tasks with Kanban boards.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-gray-300">Project Name</Label>
              <Input
                id="name"
                value={newProjectName}
                onChange={(e) => setNewProjectName(e.target.value)}
                placeholder="e.g. Product Launch"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500"
                data-testid="project-name-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="text-gray-300">Description (optional)</Label>
              <Textarea
                id="description"
                value={newProjectDesc}
                onChange={(e) => setNewProjectDesc(e.target.value)}
                placeholder="What's this project about?"
                className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 resize-none"
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setIsCreateOpen(false)}
              className="text-gray-400 hover:text-white hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateProject}
              disabled={!newProjectName.trim() || isCreating}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="create-project-submit"
            >
              {isCreating ? 'Creating...' : 'Create Project'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPage;
